import { useEffect } from "react";

/**
 * <HeroViewportFallback>
 *
 * Runtime safety net for the hero's `100svh` / `100vh` sizing.
 *
 * WHY THIS EXISTS
 * ---------------
 * The hero relies on `height: 100svh` (with `100vh` as a static-CSS fallback)
 * so it fills the visual viewport without jumping when the mobile address
 * bar shows / hides. Most modern browsers honour this, but a non-trivial
 * tail still misbehaves in production:
 *   • Old Android WebView (≤ Android 9) drops `svh` AND miscomputes `vh` when
 *     it doesn't recognise the unit token, leaving the section at 0 px tall.
 *   • Some in-app browsers (Facebook, Instagram, LinkedIn) report a
 *     stale `vh` value during the address-bar transition, briefly
 *     collapsing the hero to a few pixels.
 *   • Samsung Internet on certain Galaxy ROMs reports `100svh === 0` until
 *     the first user scroll.
 *   • Chrome on iOS (which is WebKit under the hood) occasionally returns
 *     `0` for `svh` immediately after orientation change.
 *
 * The static CSS in `index.css` already declares both `vh` and `svh`, but
 * if the parser silently accepts the unit yet the layout engine resolves
 * it to 0, no amount of CSS can save us — we need a JS probe.
 *
 * STRATEGY
 * --------
 * After mount we measure the actual rendered height of `[data-hero]`. If it
 * is suspiciously small (< 320 px or < 40% of `window.innerHeight`), we:
 *   1. Set `--hero-fallback-h: ${innerHeight}px` on `:root` so the fallback
 *      CSS class can use a concrete pixel value.
 *   2. Add `data-hero-vh-fallback="1"` on `<html>` so the matching CSS rule
 *      in `index.css` swaps the hero from `100svh` to the JS-measured
 *      `min-height` strategy.
 * The probe re-runs on `resize`, `orientationchange` and (when available)
 * `visualViewport` events, with a debounce so address-bar animations don't
 * thrash the layout.
 *
 * Renders nothing.
 */

/** Heuristics: hero shorter than this is almost certainly a unit failure. */
const MIN_PLAUSIBLE_HERO_PX = 320;
/** Or: hero takes < this fraction of the actual viewport. */
const MIN_HERO_FRACTION = 0.4;
/** Debounce window (ms) — short enough to feel instant, long enough to skip
 *  the noisy address-bar tween frames. */
const PROBE_DEBOUNCE_MS = 120;

function measureLockHeight(): number {
  const vv = window.visualViewport;
  // Prefer visualViewport.height when available — it tracks the actual
  // visible area as the address bar shows/hides, giving Chrome and
  // Opera the SAME number to lock to. Fall back to documentElement /
  // innerHeight for engines without visualViewport.
  return Math.max(
    vv ? Math.round(vv.height) : 0,
    window.innerHeight || 0,
    document.documentElement.clientHeight || 0,
    MIN_PLAUSIBLE_HERO_PX,
  );
}

function probeAndApplyFallback() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const hero = document.querySelector("[data-hero]") as HTMLElement | null;
  // No hero on this route (e.g. /admin, /blog/:slug) → nothing to do.
  if (!hero) return;

  const root = document.documentElement;
  const innerH = window.innerHeight || document.documentElement.clientHeight || 0;

  // ── ALWAYS-ON LOCK ──────────────────────────────────────────────────
  // Publish a JS-measured pixel height on every probe so Chrome Android,
  // Opera Mobile, Samsung Internet and Safari mobile resolve the hero
  // to the exact same number — no engine-specific svh/vh divergence.
  // The CSS rule on [data-hero] consumes --hero-lock-h with a 100svh
  // fallback for the SSR / pre-hydration frame.
  const lockH = measureLockHeight();
  const prevLockRaw = root.style.getPropertyValue("--hero-lock-h");
  const prevLockNum = parseInt(prevLockRaw, 10);
  // Avoid micro-updates: only rewrite the CSS var when the viewport
  // height changes by more than 4px. This prevents a write→layout-read
  // feedback loop with `visualViewport.scroll` events (mobile address-bar
  // animation), which Lighthouse flags as a forced reflow.
  if (!Number.isFinite(prevLockNum) || Math.abs(prevLockNum - lockH) > 4) {
    root.style.setProperty("--hero-lock-h", `${lockH}px`);
  }

  // ── COLLAPSE DETECTION ──────────────────────────────────────────────
  // Only run the expensive getBoundingClientRect probe on the very first
  // measurements. Repeating it on every visualViewport scroll/resize tick
  // forces a synchronous layout (Lighthouse "Forced reflow"). Once we know
  // the engine renders the hero correctly, we skip the read entirely.
  if (!root.hasAttribute("data-hero-vh-checked")) {
    const heroH = hero.getBoundingClientRect().height;
    const collapsed =
      innerH > 0 &&
      (heroH < MIN_PLAUSIBLE_HERO_PX || heroH / innerH < MIN_HERO_FRACTION);

    if (collapsed) {
      root.style.setProperty("--hero-fallback-h", `${lockH}px`);
      root.setAttribute("data-hero-vh-fallback", "1");
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          `[HeroViewportFallback] svh/vh unsupported or 0 (heroH=${heroH.toFixed(1)}px, ` +
            `innerH=${innerH}px). Falling back to ${lockH}px.`,
        );
      }
    } else {
      // Mark as healthy so subsequent probes skip the layout read.
      root.setAttribute("data-hero-vh-checked", "1");
    }
  }
}

const HeroViewportFallback = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(probeAndApplyFallback, PROBE_DEBOUNCE_MS);
    };

    // Run once after first paint so the hero has been laid out.
    // requestAnimationFrame ensures we measure post-layout, not during
    // the React commit phase.
    const raf = requestAnimationFrame(() => {
      probeAndApplyFallback();
      // And once more after fonts/poster decode settle.
      setTimeout(probeAndApplyFallback, 250);
    });

    window.addEventListener("resize", schedule, { passive: true });
    window.addEventListener("orientationchange", schedule, { passive: true });
    const vv = window.visualViewport;
    vv?.addEventListener("resize", schedule);
    vv?.addEventListener("scroll", schedule);

    return () => {
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      vv?.removeEventListener("resize", schedule);
      vv?.removeEventListener("scroll", schedule);
    };
  }, []);

  return null;
};

export default HeroViewportFallback;
